import {
  Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell,
  HeadingLevel, WidthType,
} from 'docx';

// Computes "Jul 1–7" style label for a given parallel-run week number
export function weekDateRange(weekNum: number): string {
  const startMs = new Date('2026-07-01').getTime() + (weekNum - 1) * 7 * 86_400_000;
  const endMs = startMs + 6 * 86_400_000;
  const fmt = (ms: number) =>
    new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${fmt(startMs)}–${fmt(endMs)}`;
}

interface BreakdownSide {
  total: number;
  avg_score_pct: number;
  high: number;
  mid: number;
  low: number;
}

type Breakdown = { proxy: BreakdownSide; corp_action: BreakdownSide; regulatory: BreakdownSide; issuer: BreakdownSide };

const BREAKDOWN_LABELS: [keyof Breakdown, string][] = [
  ['proxy', 'Proxy'], ['corp_action', 'Corp Action'], ['regulatory', 'Regulatory'], ['issuer', 'Issuer'],
];

// Category Breakdown table rows, shared by every export format.
export function breakdownRows(b: Breakdown): string[][] {
  return BREAKDOWN_LABELS.map(([key, label]) => {
    const s = b[key];
    return [label, String(s.total), `${s.avg_score_pct}%`, String(s.high), String(s.mid), String(s.low)];
  });
}

export function perfectScans(b: Breakdown): number {
  return BREAKDOWN_LABELS.reduce((n, [key]) => n + b[key].high, 0);
}

export interface ExportParams {
  period: string;
  // True when the dashboard had no real extraction activity and fell back to
  // sample data — exports must carry the same disclosure the UI shows.
  isMock?: boolean;
  summary: { total_receipts: number; overall_avg_score_pct: number };
  breakdown?: Breakdown;
  fieldAcc: { label: string; correct_pct: number; total: number }[];
  weekScans: {
    scan_id: string;
    client_name: string;
    date: string;
    accuracy_pct: number;
    fields_correct: number;
    fields_total: number;
    issues: string[];
  }[];
}

function tRow(cells: string[], header = false): TableRow {
  return new TableRow({
    tableHeader: header,
    children: cells.map(text =>
      new TableCell({
        children: [new Paragraph({ children: [new TextRun({ text, bold: header })] })],
      })
    ),
  });
}

function tbl(headers: string[], rows: string[][]): Table {
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: [tRow(headers, true), ...rows.map(r => tRow(r))],
  });
}

function h1(text: string): Paragraph {
  return new Paragraph({ text, heading: HeadingLevel.HEADING_1 });
}

function gap(): Paragraph {
  return new Paragraph({ text: '' });
}

export async function exportWeeklyReport(params: ExportParams): Promise<void> {
  const { period, summary, breakdown, fieldAcc, weekScans } = params;

  const children: (Paragraph | Table)[] = [
    new Paragraph({ text: 'Scan Performance Report', heading: HeadingLevel.TITLE }),
    new Paragraph({
      children: [new TextRun({ text: period, color: '6B7280' })],
    }),
    gap(),
    h1('Summary'),
    tbl(
      ['Metric', 'Value'],
      [
        ['Total Scans Processed', String(summary.total_receipts)],
        ['Overall Field Accuracy', `${summary.overall_avg_score_pct}%`],
        ['100% Accurate Scans',
          breakdown ? String(perfectScans(breakdown)) : '—'],
        ['Weakest Field',
          fieldAcc.length > 0 ? `${fieldAcc[0].label} (${fieldAcc[0].correct_pct}%)` : '—'],
      ],
    ),
    gap(),
  ];

  if (breakdown) {
    children.push(
      h1('Category Breakdown'),
      tbl(
        ['Category', 'Total Scans', 'Avg Accuracy', 'Perfect (100%)', 'Mid (80–99%)', 'Low (<80%)'],
        breakdownRows(breakdown),
      ),
      gap(),
    );
  }

  if (fieldAcc.length > 0) {
    children.push(
      h1('Field-Level Accuracy'),
      tbl(
        ['Field', 'Accuracy', 'Scans Analyzed'],
        fieldAcc.map(f => [f.label, `${f.correct_pct}%`, String(f.total)]),
      ),
      gap(),
    );
  }

  if (weekScans.length > 0) {
    children.push(
      h1('Scan Details'),
      tbl(
        ['Scan ID', 'Client', 'Date', 'Accuracy', 'Score', 'Issues'],
        weekScans.map(s => [
          s.scan_id,
          s.client_name,
          s.date,
          `${s.accuracy_pct}%`,
          `${s.fields_correct}/${s.fields_total}`,
          s.issues.length > 0 ? s.issues.join(', ') : '—',
        ]),
      ),
    );
  }

  const doc = new Document({ sections: [{ children }] });
  const blob = await Packer.toBlob(doc);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const slug = period.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'report';
  a.download = `scan-report-${slug}.docx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
